/**
 * Client Certificate API Client
 * ISO 27001 A.9.4.2 - Secure authentication mechanisms (mTLS)
 */

import { safeFetch } from '@/utils/api';

export interface ClientCertificate {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  subject: string;
  issuer: string;
  serialNumber: string;
  thumbprint: string;
  notBefore: string;
  notAfter: string;
  isActive: boolean;
  revokedAt: string | null;
  revokedReason: string | null;
  lastUsedAt: string | null;
  usageCount: number;
  lastUsedIp: string | null;
  scopes: string[];
  allowedIps: string[];
  rateLimit: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  Creator?: {
    id: string;
    email: string;
    fullName: string | null;
  } | null;
}

export interface CertificateUploadInput {
  name: string;
  description?: string;
  certificatePem: string;
  scopes?: string[];
  allowedIps?: string[];
  rateLimit?: number;
}

export interface CertificateUpdateInput {
  name?: string;
  description?: string;
  scopes?: string[];
  allowedIps?: string[];
  rateLimit?: number;
  isActive?: boolean;
}

export interface CertificateStats {
  total: number;
  active: number;
  expired: number;
  revoked: number;
  recentUsage: number;
}

export interface CertificateUsageLog {
  id: string;
  certificateId: string;
  tenantId: string;
  endpoint: string;
  method: string;
  statusCode: number;
  responseTime: number | null;
  ipAddress: string;
  userAgent: string | null;
  errorMessage: string | null;
  createdAt: string;
}

const API_BASE = '/api/v1/core';

export const clientCertificateApi = {
  /**
   * Upload a new certificate
   */
  async uploadCertificate(data: CertificateUploadInput): Promise<ClientCertificate> {
    const response = await safeFetch(`${API_BASE}/certificates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const result = await response.json();
    return result.data;
  },

  /**
   * Get all certificates
   */
  async getCertificates(options?: {
    isActive?: boolean;
    includeExpired?: boolean;
  }): Promise<ClientCertificate[]> {
    const params = new URLSearchParams();
    if (options?.isActive !== undefined) params.append('isActive', String(options.isActive));
    if (options?.includeExpired) params.append('includeExpired', 'true');
    const queryString = params.toString() ? `?${params.toString()}` : '';

    const response = await safeFetch(`${API_BASE}/certificates${queryString}`);
    const result = await response.json();
    return result.data;
  },

  /**
   * Get a specific certificate
   */
  async getCertificate(id: string): Promise<ClientCertificate> {
    const response = await safeFetch(`${API_BASE}/certificates/${id}`);
    const result = await response.json();
    return result.data;
  },

  /**
   * Update certificate settings
   */
  async updateCertificate(id: string, data: CertificateUpdateInput): Promise<ClientCertificate> {
    const response = await safeFetch(`${API_BASE}/certificates/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const result = await response.json();
    return result.data;
  },

  /**
   * Revoke a certificate
   */
  async revokeCertificate(id: string, reason: string): Promise<ClientCertificate> {
    const response = await safeFetch(`${API_BASE}/certificates/${id}/revoke`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    const result = await response.json();
    return result.data;
  },

  /**
   * Delete a certificate
   */
  async deleteCertificate(id: string): Promise<void> {
    await safeFetch(`${API_BASE}/certificates/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get certificate usage logs
   */
  async getUsageLogs(id: string, options?: {
    limit?: number;
    offset?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<CertificateUsageLog[]> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.offset) params.append('offset', String(options.offset));
    if (options?.startDate) params.append('startDate', options.startDate);
    if (options?.endDate) params.append('endDate', options.endDate);
    const queryString = params.toString() ? `?${params.toString()}` : '';

    const response = await safeFetch(`${API_BASE}/certificates/${id}/logs${queryString}`);
    const result = await response.json();
    return result.data;
  },

  /**
   * Get certificate statistics
   */
  async getStats(): Promise<CertificateStats> {
    const response = await safeFetch(`${API_BASE}/certificates/stats`);
    const result = await response.json();
    return result.data;
  },

  /**
   * Get certificates expiring soon
   */
  async getExpiringSoon(days?: number): Promise<ClientCertificate[]> {
    const params = days ? `?days=${days}` : '';
    const response = await safeFetch(`${API_BASE}/certificates/expiring${params}`);
    const result = await response.json();
    return result.data;
  },
};
